import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import {
  isAudioOverviewTerminal,
  type ArticleAudioOverviewBlockedBy,
  type ArticleAudioOverviewResponse,
  type AudioOverviewConfig,
  type AudioOverviewSourceKind,
  type AudioOverviewStatus,
  type AudioOverviewSummary,
} from '../../types/AudioOverview';

/**
 * Audio Overviews (ORA-735): the caller's feature config, every overview this
 * session has seen (keyed by id, so the panel, the library and the poller all
 * render the same copy), and the app-wide upgrade prompt.
 *
 * Blacklisted from redux-persist: statuses and signed state are server-owned
 * and time-sensitive, so a rehydrated copy would only ever be stale.
 */

export type AudioOverviewConfigStatus =
  /** Not asked yet. */
  | 'idle'
  | 'loading'
  | 'ready'
  /** A bare 404: the feature is off for this account. Hide it everywhere. */
  | 'hidden'
  /** Network or server failure; also hidden, but asked again on next use. */
  | 'error';

export type ArticleAudioOverviewStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * The lookup of Ora's shared episode for one lecture or course day folder
 * (`GET /api/v5/audio-overviews/lectures/:id`). `overviewId` is the READY
 * shared episode, or null when there is none.
 */
export interface LectureSharedAudioOverviewLookup {
  status: 'loading' | 'ready' | 'error';
  overviewId: string | null;
}

export interface ArticleAudioOverviewState {
  status: ArticleAudioOverviewStatus;
  overviewId: string | null;
  locked: boolean;
  canGenerate: boolean;
  blockedBy: ArticleAudioOverviewBlockedBy | null;
}

export interface AudioOverviewsState {
  config: {
    status: AudioOverviewConfigStatus;
    value: AudioOverviewConfig | null;
  };
  entities: Record<string, AudioOverviewSummary>;
  articles: Record<string, ArticleAudioOverviewState>;
  /**
   * Article episodes (shared by every student) this session asked to
   * generate or retry. Only these toast when they finish (ORA-905).
   */
  requestedArticleOverviewIds: Record<string, true>;
  /**
   * Stamp of the last local change per overview (create, retry, a refused
   * retry). A server response whose request started earlier is older than
   * that change and is dropped for that overview.
   */
  mutatedAt: Record<string, number>;
  /** Deleted this session: no late response may bring one back. */
  deletedIds: Record<string, true>;
  /** Bumped when every list must be fetched again (e.g. the plan changed). */
  listEpoch: number;
  /**
   * How the last list fetch for a selection ended, by its sorted lecture-id
   * key (ORA-824). The utility-row pill offers Create only once the list has
   * loaded, so it never offers it for a selection that has an episode. A
   * refetch keeps the last outcome until it ends, so the pill does not blink.
   */
  listStatusByKey: Record<string, 'ready' | 'error'>;
  /**
   * Shared-episode lookups by lecture or day folder id. The section's shared
   * row fetches them; the utility-row pill reads them, so it plays that
   * episode instead of offering Create, without a second request.
   */
  sharedLookupsById: Record<string, LectureSharedAudioOverviewLookup>;
  /** The options form (one per preview; the tile and header both open it). */
  optionsOpen: boolean;
  /** `sourceKind`: what the paywall was opened for, which picks its copy. */
  upgradePrompt: { open: boolean; sourceKind: AudioOverviewSourceKind };
}

const initialState: AudioOverviewsState = {
  config: { status: 'idle', value: null },
  entities: {},
  articles: {},
  requestedArticleOverviewIds: {},
  mutatedAt: {},
  deletedIds: {},
  listEpoch: 0,
  listStatusByKey: {},
  sharedLookupsById: {},
  optionsOpen: false,
  upgradePrompt: { open: false, sourceKind: 'lecture' },
};

let lastStamp = 0;

/**
 * A monotonic stamp. Take one just before a list or poll request starts and
 * pass it as `requestedAt`; local changes take one when they are dispatched.
 */
export const nextAudioOverviewStamp = (): number => {
  lastStamp += 1;
  return lastStamp;
};

const STATUS_RANK: Record<AudioOverviewStatus, number> = {
  QUEUED: 0,
  SCRIPTING: 1,
  SYNTHESIZING: 2,
  STITCHING: 3,
  READY: 4,
  FAILED: 4,
};

/**
 * A list page fetched before a poll can land after it. Generation only moves
 * forward, so an older status never replaces a newer one; `overviewReplaced`
 * is for the one legitimate step back (a retry, FAILED → QUEUED).
 */
const isStale = (
  existing: AudioOverviewSummary | undefined,
  incoming: AudioOverviewSummary
) => {
  if (!existing) return false;
  if (STATUS_RANK[incoming.status] < STATUS_RANK[existing.status]) return true;
  return (
    incoming.status === existing.status &&
    !isAudioOverviewTerminal(incoming.status) &&
    incoming.progress < existing.progress
  );
};

const audioOverviewsSlice = createSlice({
  name: 'audioOverviews',
  initialState,
  reducers: {
    audioOverviewConfigRequested(state) {
      // A refresh keeps the current config on screen.
      if (state.config.status !== 'ready') state.config.status = 'loading';
    },
    /** `null` means the feature is hidden for this account. */
    audioOverviewConfigReceived(
      state,
      action: PayloadAction<AudioOverviewConfig | null>
    ) {
      state.config =
        action.payload === null
          ? { status: 'hidden', value: null }
          : { status: 'ready', value: action.payload };
    },
    audioOverviewConfigFailed(state) {
      // A failed refresh keeps the config it already has.
      if (state.config.status !== 'ready') state.config.status = 'error';
    },
    articleAudioOverviewRequested(state, action: PayloadAction<string>) {
      const current = state.articles[action.payload];

      state.articles[action.payload] = current
        ? { ...current, status: 'loading' }
        : {
            status: 'loading',
            overviewId: null,
            locked: false,
            canGenerate: false,
            blockedBy: null,
          };
    },
    articleAudioOverviewReceived(
      state,
      action: PayloadAction<{
        articleId: string;
        result: ArticleAudioOverviewResponse;
        /** Stamp taken just before the GET started. */
        requestedAt: number;
      }>
    ) {
      const { articleId, result, requestedAt } = action.payload;
      const { overview } = result;
      if (overview) {
        // The article says the episode exists, whatever a poll concluded.
        delete state.deletedIds[overview.id];

        // Same guards as `audioOverviewsUpserted`: never older than a local
        // change (a retry's QUEUED) or than a status already seen.
        const isOutdated =
          requestedAt < (state.mutatedAt[overview.id] ?? 0) ||
          isStale(state.entities[overview.id], overview);

        if (!isOutdated) state.entities[overview.id] = overview;
      }

      state.articles[articleId] = {
        status: 'ready',
        overviewId: result.overview?.id ?? null,
        locked: result.locked,
        canGenerate: result.canGenerate,
        blockedBy: result.blockedBy,
      };
    },
    articleAudioOverviewLinked(
      state,
      action: PayloadAction<{ articleId: string; overviewId: string }>
    ) {
      const current = state.articles[action.payload.articleId];

      state.articles[action.payload.articleId] = {
        status: 'ready',
        overviewId: action.payload.overviewId,
        locked: current?.locked ?? false,
        canGenerate: current?.canGenerate ?? true,
        blockedBy: current?.blockedBy ?? null,
      };
    },
    /** This session asked for this article episode (create or retry). */
    articleAudioOverviewTracked(state, action: PayloadAction<string>) {
      state.requestedArticleOverviewIds[action.payload] = true;
    },
    /**
     * Stops watching an article episode the server will not show this
     * student (a lapsed plan). Unlike `audioOverviewRemoved` it is not
     * tombstoned, so the article can show it again; lecture episodes are
     * left alone.
     */
    articleAudioOverviewDropped(state, action: PayloadAction<string>) {
      if (state.entities[action.payload]?.source !== 'article') return;
      delete state.entities[action.payload];
    },
    articleAudioOverviewFailed(state, action: PayloadAction<string>) {
      const current = state.articles[action.payload];

      state.articles[action.payload] = current
        ? { ...current, status: 'error' }
        : {
            status: 'error',
            overviewId: null,
            locked: false,
            canGenerate: false,
            blockedBy: null,
          };
    },
    /** Server data from a list or poll that started at `requestedAt`. */
    audioOverviewsUpserted(
      state,
      action: PayloadAction<{
        items: AudioOverviewSummary[];
        requestedAt: number;
      }>
    ) {
      const { items, requestedAt } = action.payload;

      items.forEach((overview) => {
        if (state.deletedIds[overview.id]) return;
        if (requestedAt < (state.mutatedAt[overview.id] ?? 0)) return;
        if (isStale(state.entities[overview.id], overview)) return;
        state.entities[overview.id] = overview;
      });
    },
    /**
     * A local change's result (create, retry): unconditional, since a retry's
     * status legitimately moves back, and it outranks older responses.
     */
    audioOverviewReplaced: {
      reducer(
        state,
        action: PayloadAction<{ overview: AudioOverviewSummary; stamp: number }>
      ) {
        const { overview, stamp } = action.payload;
        if (state.deletedIds[overview.id]) return;
        state.entities[overview.id] = overview;
        state.mutatedAt[overview.id] = stamp;
      },
      prepare: (overview: AudioOverviewSummary) => ({
        payload: { overview, stamp: nextAudioOverviewStamp() },
      }),
    },
    /** A retry the server refused for good (409 NOT_RETRYABLE). */
    audioOverviewRetryRefused: {
      reducer(state, action: PayloadAction<{ id: string; stamp: number }>) {
        const overview = state.entities[action.payload.id];
        if (!overview) return;
        overview.retryable = false;
        state.mutatedAt[action.payload.id] = action.payload.stamp;
      },
      prepare: (id: string) => ({
        payload: { id, stamp: nextAudioOverviewStamp() },
      }),
    },
    /** Replay from the start: no longer "Finished" while it plays again. */
    audioOverviewReplayStarted: {
      reducer(state, action: PayloadAction<{ id: string; stamp: number }>) {
        const overview = state.entities[action.payload.id];
        if (!overview) return;
        overview.completedAt = null;
        overview.positionSec = 0;
        state.mutatedAt[action.payload.id] = action.payload.stamp;
      },
      prepare: (id: string) => ({
        payload: { id, stamp: nextAudioOverviewStamp() },
      }),
    },
    /** The options form, opened from the tile or the preview header. */
    audioOverviewOptionsOpened(state) {
      state.optionsOpen = true;
    },
    audioOverviewOptionsClosed(state) {
      state.optionsOpen = false;
    },
    audioOverviewRemoved(state, action: PayloadAction<string>) {
      const isArticle = state.entities[action.payload]?.source === 'article';
      delete state.entities[action.payload];
      // An article's shared episode is never deleted for good; its article
      // may serve it again, so it is not tombstoned (ORA-905).
      if (!isArticle) state.deletedIds[action.payload] = true;
    },
    /** A selection's list fetch ended (see `listStatusByKey`). */
    audioOverviewListSettled(
      state,
      action: PayloadAction<{ key: string; status: 'ready' | 'error' }>
    ) {
      state.listStatusByKey[action.payload.key] = action.payload.status;
    },
    /** A shared-episode lookup started or ended (see `sharedLookupsById`). */
    lectureSharedAudioOverviewLookedUp(
      state,
      action: PayloadAction<
        { lectureId: string } & LectureSharedAudioOverviewLookup
      >
    ) {
      const { lectureId, status, overviewId } = action.payload;
      state.sharedLookupsById[lectureId] = { status, overviewId };
    },
    /** The plan changed (locked flipped): every list refetches. */
    audioOverviewListsInvalidated(state) {
      state.listEpoch += 1;
    },
    /** A different account: forget everything. */
    audioOverviewsReset() {
      return initialState;
    },
    /** Keeps Resume honest between list fetches. */
    audioOverviewProgressSaved(
      state,
      action: PayloadAction<{
        id: string;
        positionSec: number;
        completed: boolean;
      }>
    ) {
      const overview = state.entities[action.payload.id];
      if (!overview) return;
      overview.positionSec = action.payload.positionSec;
      if (action.payload.completed && !overview.completedAt) {
        overview.completedAt = new Date().toISOString();
      }
    },
    audioOverviewUpgradePromptOpened: {
      reducer(state, action: PayloadAction<AudioOverviewSourceKind>) {
        state.upgradePrompt = { open: true, sourceKind: action.payload };
      },
      prepare: (sourceKind: AudioOverviewSourceKind = 'lecture') => ({
        payload: sourceKind,
      }),
    },
    audioOverviewUpgradePromptClosed(state) {
      state.upgradePrompt.open = false;
    },
  },
});

export const {
  audioOverviewConfigRequested,
  audioOverviewConfigReceived,
  audioOverviewConfigFailed,
  articleAudioOverviewRequested,
  articleAudioOverviewReceived,
  articleAudioOverviewLinked,
  articleAudioOverviewTracked,
  articleAudioOverviewDropped,
  articleAudioOverviewFailed,
  audioOverviewsUpserted,
  audioOverviewReplaced,
  audioOverviewRetryRefused,
  audioOverviewReplayStarted,
  audioOverviewOptionsOpened,
  audioOverviewOptionsClosed,
  audioOverviewRemoved,
  audioOverviewListSettled,
  lectureSharedAudioOverviewLookedUp,
  audioOverviewListsInvalidated,
  audioOverviewsReset,
  audioOverviewProgressSaved,
  audioOverviewUpgradePromptOpened,
  audioOverviewUpgradePromptClosed,
} = audioOverviewsSlice.actions;

export default audioOverviewsSlice.reducer;

type WithAudioOverviews = { audioOverviews: AudioOverviewsState };

export const selectAudioOverviewConfigState = (state: WithAudioOverviews) =>
  state.audioOverviews.config;

export const selectAudioOverviewEntities = (state: WithAudioOverviews) =>
  state.audioOverviews.entities;

const EMPTY_ARTICLE_AUDIO_OVERVIEW_STATE: ArticleAudioOverviewState = {
  status: 'idle',
  overviewId: null,
  locked: false,
  canGenerate: false,
  blockedBy: null,
};

export const selectArticleAudioOverviewState = (
  state: WithAudioOverviews,
  articleId: string
): ArticleAudioOverviewState =>
  state.audioOverviews.articles[articleId] ??
  EMPTY_ARTICLE_AUDIO_OVERVIEW_STATE;

export const selectArticleAudioOverview = (
  state: WithAudioOverviews,
  articleId: string
): AudioOverviewSummary | null => {
  const overviewId = state.audioOverviews.articles[articleId]?.overviewId;
  return overviewId
    ? (state.audioOverviews.entities[overviewId] ?? null)
    : null;
};

export const selectRequestedArticleOverviewIds = (state: WithAudioOverviews) =>
  state.audioOverviews.requestedArticleOverviewIds;

export const selectAudioOverviewListEpoch = (state: WithAudioOverviews) =>
  state.audioOverviews.listEpoch;

/** The sorted, comma-joined lecture ids that key a selection's list. */
export const audioOverviewSelectionKey = (lectureIds: readonly string[]) =>
  [...lectureIds].sort().join(',');

export const selectAudioOverviewListStatus = (
  state: WithAudioOverviews,
  key: string
): 'ready' | 'error' | undefined => state.audioOverviews.listStatusByKey[key];

export const selectLectureSharedAudioOverviewLookup = (
  state: WithAudioOverviews,
  lectureId: string | null
): LectureSharedAudioOverviewLookup | undefined =>
  lectureId ? state.audioOverviews.sharedLookupsById[lectureId] : undefined;

/** The lecture's (or day's) READY shared episode, or null. */
export const selectLectureSharedAudioOverview = (
  state: WithAudioOverviews,
  lectureId: string | null
): AudioOverviewSummary | null => {
  const overviewId = selectLectureSharedAudioOverviewLookup(
    state,
    lectureId
  )?.overviewId;

  const overview = overviewId
    ? state.audioOverviews.entities[overviewId]
    : null;

  return overview?.shared && overview.status === 'READY' ? overview : null;
};

export const selectAudioOverviewOptionsOpen = (state: WithAudioOverviews) =>
  state.audioOverviews.optionsOpen;

export const selectAudioOverviewUpgradePromptOpen = (
  state: WithAudioOverviews
) => state.audioOverviews.upgradePrompt.open;

export const selectAudioOverviewUpgradePromptSourceKind = (
  state: WithAudioOverviews
) => state.audioOverviews.upgradePrompt.sourceKind;

const byNewest = (a: AudioOverviewSummary, b: AudioOverviewSummary) =>
  b.createdAt.localeCompare(a.createdAt);

/** Every overview still generating, newest first. */
export const selectPendingAudioOverviews = createSelector(
  [selectAudioOverviewEntities],
  (entities) =>
    Object.values(entities)
      .filter((overview) => !isAudioOverviewTerminal(overview.status))
      .sort(byNewest)
);

/**
 * The caller's own overviews that include ANY of the given lectures, newest
 * first. Shared episodes are shown on their own, never as the student's.
 */
export const selectAudioOverviewsForLectures = createSelector(
  [
    selectAudioOverviewEntities,
    (_state: WithAudioOverviews, lectureIds: readonly string[]) => lectureIds,
  ],
  (entities, lectureIds) => {
    const wanted = new Set(lectureIds);
    return Object.values(entities)
      .filter(
        (overview) =>
          !overview.shared &&
          overview.lectures.some((lecture) => wanted.has(lecture.id))
      )
      .sort(byNewest);
  }
);

/**
 * The newest overview whose lecture set is exactly `lectureIds` (order and
 * repeats ignored), or null (ORA-810). The server allows one live episode
 * per set; older duplicates can exist, and the newest one wins.
 */
export const selectAudioOverviewForExactLectures = createSelector(
  [
    selectAudioOverviewEntities,
    (_state: WithAudioOverviews, lectureIds: readonly string[]) => lectureIds,
  ],
  (entities, lectureIds): AudioOverviewSummary | null => {
    const wanted = new Set(lectureIds);
    if (wanted.size === 0) return null;

    const matches = Object.values(entities).filter((overview) => {
      // A shared episode never counts as the student's own for the selection.
      if (overview.shared) return false;
      const ids = new Set(overview.lectures.map((lecture) => lecture.id));
      return ids.size === wanted.size && [...ids].every((id) => wanted.has(id));
    });

    return matches.sort(byNewest)[0] ?? null;
  }
);
